/**
 * Transforms a Supabase storage URL to use the image render/transform API
 * for on-the-fly resizing and WebP conversion, reducing bandwidth for property card images.
 */
export function getOptimizedImageUrl(url: string, width: number, quality = 60): string {
  if (!url) return url;

  // Unsplash images support on-the-fly format and size parameters
  if (url.includes('images.unsplash.com')) {
    try {
      const u = new URL(url);
      u.searchParams.set('auto', 'format');
      u.searchParams.set('fm', 'webp');
      if (width) u.searchParams.set('w', String(width));
      if (quality) u.searchParams.set('q', String(quality));
      return u.toString();
    } catch {
      return url;
    }
  }

  // Encode path segments so filenames with spaces or other special chars
  // don't break <img srcset> parsing (spaces there separate URL from descriptor).
  const encodePath = (path: string) =>
    path
      .split('/')
      .map((seg) => {
        try {
          // If already percent-encoded, leave as-is; otherwise encode.
          return /%[0-9A-Fa-f]{2}/.test(seg) ? seg : encodeURIComponent(seg);
        } catch {
          return seg;
        }
      })
      .join('/');

  // Rewrite legacy Supabase project URLs to the current project
  const currentBase = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (currentBase) {
    const legacyPattern = /^https:\/\/[a-z0-9]+\.supabase\.co\/storage\/v1\/(object|render\/image)\/public\/(.+?)(\?.*)?$/;
    const legacyMatch = url.match(legacyPattern);
    if (legacyMatch) {
      const path = encodePath(legacyMatch[2]);
      return `${currentBase}/storage/v1/render/image/public/${path}?width=${width}&resize=contain&quality=${quality}&format=webp`;
    }
  }

  // Only transform Supabase storage public URLs
  const supabasePattern = /^(https:\/\/[^/]+\.supabase\.co)\/storage\/v1\/object\/public\/(.+)$/;
  const match = url.match(supabasePattern);
  if (match) {
    const [, baseUrl, rawPath] = match;
    const path = encodePath(rawPath.split('?')[0]);
    return `${baseUrl}/storage/v1/render/image/public/${path}?width=${width}&resize=contain&quality=${quality}&format=webp`;
  }

  // Return original URL for other images
  return url;
}
